/**
 * Pheromone grid for biologically plausible trail foraging behavior.
 *
 * Implements:
 * - P_trail: Food trail pheromone (laid by returning ants)
 * - P_alarm: Avoidance/danger pheromone (laid near hazards/deaths)
 * - NestScent: Static distance field for robust homing
 *
 * Pheromones evaporate over time and diffuse slightly to neighbors.
 */

import { Canonical } from '../hashState';

export interface PheromoneCell {
  trail: number;      // Food trail pheromone (0-1)
  alarm: number;      // Danger/avoidance pheromone (0-1)
  nestScent: number;  // Static distance to nest (higher = closer)
  foodOdor: number;   // Food odor scalar field (0-1), recomputed from food sources
}

export interface TerrainModifiers {
  costMultiplier: number;     // Movement cost (1 = normal, >1 = slower)
  pheromoneRetention: number; // Multiplier for evaporation (1 = normal, <1 = faster evap)
  blocked: boolean;
  dangerous: boolean;
  slippery: boolean;          // Adds noise to steering
}

/**
 * 8-way direction enum for ant heading.
 */
export enum Direction8 {
  N = 0,
  NE = 1,
  E = 2,
  SE = 3,
  S = 4,
  SW = 5,
  W = 6,
  NW = 7,
}

/**
 * Direction vectors for 8-way movement.
 */
export const DIR_VECTORS: Record<Direction8, { dx: number; dy: number }> = {
  [Direction8.N]: { dx: 0, dy: -1 },
  [Direction8.NE]: { dx: 1, dy: -1 },
  [Direction8.E]: { dx: 1, dy: 0 },
  [Direction8.SE]: { dx: 1, dy: 1 },
  [Direction8.S]: { dx: 0, dy: 1 },
  [Direction8.SW]: { dx: -1, dy: 1 },
  [Direction8.W]: { dx: -1, dy: 0 },
  [Direction8.NW]: { dx: -1, dy: -1 },
};

/**
 * Get the cell offset for a sensor position relative to heading.
 * F = front, FL = front-left, FR = front-right, L = left, R = right
 */
export function getSensorOffset(
  heading: Direction8,
  sensor: 'F' | 'FL' | 'FR' | 'L' | 'R'
): { dx: number; dy: number } {
  const front = DIR_VECTORS[heading];
  const left = DIR_VECTORS[(heading + 6) % 8 as Direction8]; // -2 = left 90°
  const right = DIR_VECTORS[(heading + 2) % 8 as Direction8]; // +2 = right 90°
  const frontLeft = DIR_VECTORS[(heading + 7) % 8 as Direction8]; // -1 = 45° left
  const frontRight = DIR_VECTORS[(heading + 1) % 8 as Direction8]; // +1 = 45° right

  switch (sensor) {
    case 'F': return front;
    case 'FL': return frontLeft;
    case 'FR': return frontRight;
    case 'L': return left;
    case 'R': return right;
  }
}

/**
 * Turn direction by steps (-1 = left, +1 = right).
 */
export function turnDirection(dir: Direction8, steps: number): Direction8 {
  return ((dir + steps + 8) % 8) as Direction8;
}

/**
 * PheromoneGrid manages pheromone fields for ant trail behavior.
 */
export interface PheromoneGridConfig {
  EVAPORATION_RATE: number;
  DIFFUSION_RATE: number;
  TRAIL_DEPOSIT_AMOUNT: number;
  ALARM_DEPOSIT_AMOUNT: number;
  TRAIL_FOLLOW_THRESHOLD: number;
  TRAIL_CONFIDENCE_GAIN: number;
  TRAIL_CONFIDENCE_DECAY: number;
  FOOD_ODOR_RADIUS: number;
  FOOD_ODOR_STRENGTH: number;
}

/**
 * The four fields, flattened row-major. See `PheromoneGrid.save()`.
 *
 * One array per field rather than one object per cell: a district is 1,200
 * cells, and four arrays of numbers is a tenth the size of 1,200 four-key
 * objects once it reaches JSON.
 */
export interface PheromoneSnapshot {
  cols: number;
  rows: number;
  trail: number[];
  alarm: number[];
  nestScent: number[];
  foodOdor: number[];
}

export const DEFAULT_PHEROMONE_CONFIG: PheromoneGridConfig = {
  EVAPORATION_RATE: 0.002,
  DIFFUSION_RATE: 0.01,
  TRAIL_DEPOSIT_AMOUNT: 0.15,
  ALARM_DEPOSIT_AMOUNT: 0.3,
  TRAIL_FOLLOW_THRESHOLD: 0.05,
  TRAIL_CONFIDENCE_GAIN: 0.1,
  TRAIL_CONFIDENCE_DECAY: 0.05,
  FOOD_ODOR_RADIUS: 8,
  FOOD_ODOR_STRENGTH: 1.0,
};

export class PheromoneGrid {
  private cells: PheromoneCell[][] = [];
  private readonly cols: number;
  private readonly rows: number;
  private readonly MAX_PHEROMONE = 1.0;
  private readonly MIN_PHEROMONE = 0.001;
  private pConfig: PheromoneGridConfig;

  // Backward-compatible public accessors
  get TRAIL_DEPOSIT_AMOUNT(): number { return this.pConfig.TRAIL_DEPOSIT_AMOUNT; }
  get ALARM_DEPOSIT_AMOUNT(): number { return this.pConfig.ALARM_DEPOSIT_AMOUNT; }
  get TRAIL_FOLLOW_THRESHOLD(): number { return this.pConfig.TRAIL_FOLLOW_THRESHOLD; }
  get TRAIL_CONFIDENCE_GAIN(): number { return this.pConfig.TRAIL_CONFIDENCE_GAIN; }
  get TRAIL_CONFIDENCE_DECAY(): number { return this.pConfig.TRAIL_CONFIDENCE_DECAY; }

  /**
   * Cached `exp(-dist / radius)` falloff, indexed by integer cell offset.
   *
   * Food sources sit on integer grid positions, so the falloff from a source to a
   * cell is a function of two integers and a constant radius — the same few
   * hundred values, recomputed with a `sqrt` and an `exp` ten times a sim-second
   * for every fruiting bush on the map. In an undeveloped district that is three
   * hundred bushes over the whole grid, and it dominated the surface's cost.
   *
   * The table holds exactly the values the arithmetic produced, so this is a
   * speed-up and not a behaviour change: the golden masters do not move.
   */
  private odorKernelTable: Float64Array | null = null;
  private odorKernelRadius = -1;

  private odorKernel(radius: number, reach: number): Float64Array {
    if (this.odorKernelTable && this.odorKernelRadius === radius) {
      return this.odorKernelTable;
    }
    const span = reach * 2 + 1;
    const table = new Float64Array(span * span);
    for (let dy = -reach; dy <= reach; dy++) {
      for (let dx = -reach; dx <= reach; dx++) {
        const dist = Math.sqrt(dx * dx + dy * dy);
        table[(dy + reach) * span + (dx + reach)] = Math.exp(-dist / radius);
      }
    }
    this.odorKernelTable = table;
    this.odorKernelRadius = radius;
    return table;
  }

  constructor(cols: number, rows: number, config: Partial<PheromoneGridConfig> = {}) {
    this.cols = cols;
    this.rows = rows;
    this.pConfig = { ...DEFAULT_PHEROMONE_CONFIG, ...config };
    this.initializeCells();
  }

  getConfig(): PheromoneGridConfig {
    return { ...this.pConfig };
  }

  updateConfig(partial: Partial<PheromoneGridConfig>): void {
    this.pConfig = { ...this.pConfig, ...partial };
  }

  private initializeCells(): void {
    for (let y = 0; y < this.rows; y++) {
      this.cells[y] = [];
      for (let x = 0; x < this.cols; x++) {
        this.cells[y][x] = {
          trail: 0,
          alarm: 0,
          nestScent: 0,
          foodOdor: 0,
        };
      }
    }
  }

  /**
   * Initialize nest scent field from portal positions.
   * Creates a distance-based gradient (higher = closer to nest).
   */
  initializeNestScent(portalCells: Array<{ x: number; y: number }>): void {
    // For each cell, calculate distance to nearest portal
    const maxDist = Math.sqrt(this.cols * this.cols + this.rows * this.rows);

    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        let minDist = Infinity;
        for (const portal of portalCells) {
          const dist = Math.sqrt(
            (x - portal.x) * (x - portal.x) +
            (y - portal.y) * (y - portal.y)
          );
          minDist = Math.min(minDist, dist);
        }
        // Convert distance to scent (higher = closer)
        // Use exponential falloff for more realistic gradient
        this.cells[y][x].nestScent = Math.exp(-minDist / 15);
      }
    }
  }

  /**
   * Recompute food odor field from food source positions and amounts.
   * Uses exponential falloff from each source, weighted by fruit availability.
   */
  updateFoodOdor(sources: Array<{ x: number; y: number; fruitCount: number; maxFruits: number }>): void {
    const radius = this.pConfig.FOOD_ODOR_RADIUS;
    const strength = this.pConfig.FOOD_ODOR_STRENGTH;

    // Clear food odor
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        this.cells[y][x].foodOdor = 0;
      }
    }

    // Accumulate food odor from each source
    const reach = Math.ceil(radius * 2);
    const kernel = this.odorKernel(radius, reach);
    const span = reach * 2 + 1;

    for (const src of sources) {
      if (src.fruitCount <= 0) continue;
      const weight = (src.fruitCount / src.maxFruits) * strength;

      // Only iterate within bounding box of radius
      const minX = Math.max(0, Math.floor(src.x - reach));
      const maxX = Math.min(this.cols - 1, Math.ceil(src.x + reach));
      const minY = Math.max(0, Math.floor(src.y - reach));
      const maxY = Math.min(this.rows - 1, Math.ceil(src.y + reach));

      for (let y = minY; y <= maxY; y++) {
        const row = this.cells[y];
        const kRow = (y - src.y + reach) * span;
        for (let x = minX; x <= maxX; x++) {
          row[x].foodOdor += weight * kernel[kRow + (x - src.x + reach)];
        }
      }
    }

    // Clamp to MAX_PHEROMONE
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        if (this.cells[y][x].foodOdor > this.MAX_PHEROMONE) {
          this.cells[y][x].foodOdor = this.MAX_PHEROMONE;
        }
      }
    }
  }

  /**
   * Get pheromone values at a cell.
   */
  getCell(x: number, y: number): PheromoneCell | null {
    if (x < 0 || x >= this.cols || y < 0 || y >= this.rows) {
      return null;
    }
    return this.cells[y][x];
  }

  /**
   * Deposit trail pheromone at a cell (typically by returning ants).
   */
  depositTrail(x: number, y: number, amount: number = this.TRAIL_DEPOSIT_AMOUNT): void {
    if (x < 0 || x >= this.cols || y < 0 || y >= this.rows) return;
    this.cells[y][x].trail = Math.min(
      this.MAX_PHEROMONE,
      this.cells[y][x].trail + amount
    );
  }

  /**
   * Deposit alarm pheromone at a cell (on ant death or danger).
   */
  depositAlarm(x: number, y: number, amount: number = this.ALARM_DEPOSIT_AMOUNT): void {
    if (x < 0 || x >= this.cols || y < 0 || y >= this.rows) return;
    this.cells[y][x].alarm = Math.min(
      this.MAX_PHEROMONE,
      this.cells[y][x].alarm + amount
    );
  }

  /**
   * Update pheromone evaporation and diffusion.
   * @param dt Delta time in milliseconds
   * @param getTerrainModifier Optional per-cell terrain modifier.
   *   Returns either a single number (legacy: affects evaporation only)
   *   or {evapMod, diffusionMod} for separate control.
   */
  update(
    dt: number,
    getTerrainModifier?: (x: number, y: number) => number | { evapMod: number; diffusionMod: number }
  ): void {
    const evapFactor = 1 - this.pConfig.EVAPORATION_RATE * dt;

    // Create new grid for diffusion (to avoid order-dependent updates)
    const newTrail: number[][] = [];
    const newAlarm: number[][] = [];

    for (let y = 0; y < this.rows; y++) {
      newTrail[y] = [];
      newAlarm[y] = [];
      for (let x = 0; x < this.cols; x++) {
        const cell = this.cells[y][x];

        let evapMod = 1;
        let diffusionMod = 1;
        if (getTerrainModifier) {
          const mod = getTerrainModifier(x, y);
          if (typeof mod === 'number') {
            evapMod = mod;
            diffusionMod = 1;
          } else {
            evapMod = mod.evapMod;
            diffusionMod = mod.diffusionMod;
          }
        }

        const localEvap = Math.pow(evapFactor, 1 / evapMod);

        // Evaporate
        let trail = cell.trail * localEvap;
        let alarm = cell.alarm * localEvap;

        // Diffuse from neighbors (small amount), scaled by terrain diffusion modifier
        const neighbors = this.getNeighborValues(x, y);
        if (neighbors.count > 0) {
          const avgTrail = neighbors.trailSum / neighbors.count;
          const avgAlarm = neighbors.alarmSum / neighbors.count;

          const localDiffusion = this.pConfig.DIFFUSION_RATE * diffusionMod;
          trail += (avgTrail - trail) * localDiffusion * dt * 0.001;
          alarm += (avgAlarm - alarm) * localDiffusion * dt * 0.001;
        }

        // Clamp and threshold
        newTrail[y][x] = trail < this.MIN_PHEROMONE ? 0 : Math.min(trail, this.MAX_PHEROMONE);
        newAlarm[y][x] = alarm < this.MIN_PHEROMONE ? 0 : Math.min(alarm, this.MAX_PHEROMONE);
      }
    }

    // Apply new values
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        this.cells[y][x].trail = newTrail[y][x];
        this.cells[y][x].alarm = newAlarm[y][x];
      }
    }
  }

  private getNeighborValues(x: number, y: number): { trailSum: number; alarmSum: number; count: number } {
    let trailSum = 0;
    let alarmSum = 0;
    let count = 0;

    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const nx = x + dx;
        const ny = y + dy;
        if (nx >= 0 && nx < this.cols && ny >= 0 && ny < this.rows) {
          trailSum += this.cells[ny][nx].trail;
          alarmSum += this.cells[ny][nx].alarm;
          count++;
        }
      }
    }

    return { trailSum, alarmSum, count };
  }

  /**
   * Sample pheromone at sensor positions for an ant.
   * Returns trail values at Front, Front-Left, Front-Right positions.
   */
  sampleSensors(
    x: number,
    y: number,
    heading: Direction8
  ): {
    tF: number; tFL: number; tFR: number;
    aF: number; aFL: number; aFR: number;
    nestF: number; nestFL: number; nestFR: number;
    foodF: number; foodFL: number; foodFR: number;
  } {
    const fOffset = getSensorOffset(heading, 'F');
    const flOffset = getSensorOffset(heading, 'FL');
    const frOffset = getSensorOffset(heading, 'FR');

    const cellF = this.getCell(x + fOffset.dx, y + fOffset.dy);
    const cellFL = this.getCell(x + flOffset.dx, y + flOffset.dy);
    const cellFR = this.getCell(x + frOffset.dx, y + frOffset.dy);

    return {
      tF: cellF?.trail ?? 0,
      tFL: cellFL?.trail ?? 0,
      tFR: cellFR?.trail ?? 0,
      aF: cellF?.alarm ?? 0,
      aFL: cellFL?.alarm ?? 0,
      aFR: cellFR?.alarm ?? 0,
      nestF: cellF?.nestScent ?? 0,
      nestFL: cellFL?.nestScent ?? 0,
      nestFR: cellFR?.nestScent ?? 0,
      foodF: cellF?.foodOdor ?? 0,
      foodFL: cellFL?.foodOdor ?? 0,
      foodFR: cellFR?.foodOdor ?? 0,
    };
  }

  /**
   * All four fields, complete.
   *
   * `nestScent` is included even though it is a static distance field computed
   * once from the portal positions. Recomputing it on load would work and would
   * also mean a restored world had to be told where the nests are before it
   * could be a world — a snapshot that needs an argument is not total.
   */
  save(): PheromoneSnapshot {
    const size = this.cols * this.rows;
    const trail = new Array<number>(size);
    const alarm = new Array<number>(size);
    const nestScent = new Array<number>(size);
    const foodOdor = new Array<number>(size);

    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        const i = y * this.cols + x;
        const c = this.cells[y][x];
        trail[i] = c.trail;
        alarm[i] = c.alarm;
        nestScent[i] = c.nestScent;
        foodOdor[i] = c.foodOdor;
      }
    }

    return { cols: this.cols, rows: this.rows, trail, alarm, nestScent, foodOdor };
  }

  /** Restore from `save()`. */
  load(state: PheromoneSnapshot): void {
    if (state.cols !== this.cols || state.rows !== this.rows) {
      throw new RangeError(
        `pheromone snapshot is ${state.cols}x${state.rows}, this grid is ` +
        `${this.cols}x${this.rows}`
      );
    }

    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        const i = y * this.cols + x;
        const c = this.cells[y][x];
        c.trail = state.trail[i];
        c.alarm = state.alarm[i];
        c.nestScent = state.nestScent[i];
        c.foodOdor = state.foodOdor[i];
      }
    }
  }

  /**
   * Order-stable view of the pheromone fields, for hashing.
   *
   * `nestScent` is a static distance field computed once from the portal
   * positions, so it is included: two worlds whose nests sit in different
   * places are different worlds, and this is where that shows up.
   */
  canonicalState(): Canonical {
    const rows: Canonical[] = [];
    for (let y = 0; y < this.rows; y++) {
      const row: Canonical[] = [];
      for (let x = 0; x < this.cols; x++) {
        const c = this.cells[y][x];
        row.push([c.trail, c.alarm, c.nestScent, c.foodOdor]);
      }
      rows.push(row);
    }
    return rows;
  }

  /**
   * Get dimensions.
   */
  getCols(): number { return this.cols; }
  getRows(): number { return this.rows; }

  /**
   * Get total trail pheromone (for debugging/stats).
   */
  getTotalTrail(): number {
    let total = 0;
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        total += this.cells[y][x].trail;
      }
    }
    return total;
  }
}
